// src/Components/pages/Blog.js
import React, { useState, useEffect } from "react";
import "./Blog.css";

const Blog = () => {
  const [currentBlog, setCurrentBlog] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate loading blog data
    setTimeout(() => {
      setCurrentBlog({
        id: 1,
        title: "Student Spotlight: Veer Gandhi Shines at International Robotics Competition in Beijing",
        slug: "veer-gandhi-beijing-robotics-competition",
        excerpt: "STEMTRIX student Veer Gandhi achieves remarkable success at the International Student Robotics Challenge, winning multiple awards in programming and construction categories.",
        content: blogContent,
        author: "STEMTRIX Editorial Team",
        publishDate: "August 26, 2025",
        readTime: "4 min read",
        location: "Beijing, China",
        image: "/images/blog/veer-gandhi.jpg",
        tags: ["Robotics", "Programming", "International Competition", "Student Achievement"],
        category: "Student Spotlight"
      });
      setIsLoading(false);
    }, 1000);
  }, []);

  const blogContent = `
    This summer, **Veer Gandhi** proudly represented Premier Academy at the prestigious *International Student Robotics Challenge* hosted by the University of Science and Technology Beijing (USTB). Competing against a global cohort of young innovators, Veer showcased remarkable talent, resilience, and creativity across two demanding categories: **RC.Code (Scratch Programming)** and **WeDo Construction (Mechanical Design & Storytelling)**.

    ## Outstanding Achievements

    In the **Scratch Programming category**, Veer's solution stood out for its elegant logic, intuitive interface, and creative execution—earning him **Second Place**. Judges praised his ability to integrate functionality with user-friendly design, a testament to his growing expertise in coding and digital problem-solving.

    In the **WeDo Construction category**, Veer independently designed, constructed, and presented a fully functional model that earned him **Third Place**. His project combined mechanical precision with imaginative storytelling, capturing the spirit of innovation and creativity.

    ## Overcoming Challenges

    Despite the language barrier and absence of his teammate, Veer adapted with courage and determination—managing technical execution and presentation with poise. This demonstrates not only his technical skills but also his resilience and adaptability under pressure.

    ## The STEMTRIX Difference

    Veer's performance in Beijing reflects the quality of education and mentorship provided by **STEMTRIX**. Our comprehensive STEM programs prepare students not just with technical knowledge, but with the confidence and problem-solving skills needed to excel on the global stage.

    His achievement is a testament to what's possible when passionate students receive world-class STEM education. We are incredibly proud of Veer's accomplishment and excited to see where his journey in robotics and innovation will lead him next!
  `;

  const relatedArticles = [
    {
      id: 2,
      title: "STEMTRIX Students Excel at National Robotics Championship",
      excerpt: "Our students dominated the national competition with innovative solutions.",
      date: "July 15, 2025",
      image: "/images/blog/national-championship.jpg",
      readTime: "3 min read"
    },
    {
      id: 3,
      title: "The Future of STEM Education in Kenya",
      excerpt: "Exploring how technology is reshaping education in East Africa.",
      date: "June 28, 2025",
      image: "/images/blog/stem-future-kenya.jpg",
      readTime: "5 min read"
    },
    {
      id: 4,
      title: "Building Tomorrow's Innovators: Our Teaching Philosophy",
      excerpt: "Discover the methodology behind STEMTRIX's success.",
      date: "June 10, 2025",
      image: "/images/blog/teaching-philosophy.jpg",
      readTime: "4 min read"
    }
  ];

  const handleShare = () => {
    if (navigator.share && currentBlog) {
      navigator.share({
        title: currentBlog.title,
        text: currentBlog.excerpt,
        url: window.location.href,
      });
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert("Link copied to clipboard!");
    }
  };

  const formatContent = (content) => {
    return content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/## (.*?)$/gm, '<h3 class="content-heading">$1</h3>')
      .replace(/\n\n/g, '</p><p>')
      .replace(/^\s*/, '<p>')
      .replace(/\s*$/, '</p>');
  };

  if (isLoading) {
    return (
      <div className="blog-loading">
        <div className="loading-spinner"></div>
        <p>Loading article...</p>
      </div>
    );
  }

  if (!currentBlog) {
    return (
      <div className="blog-error">
        <h1>Article Not Found</h1>
        <p>The article you're looking for doesn't exist.</p>
      </div>
    );
  }

  return (
    <>
      {/* SEO Meta Tags - Add these to your document head */}
      <div style={{ display: 'none' }}>
        {/* 
        <title>{currentBlog.title} | STEMTRIX Blog</title>
        <meta name="description" content={currentBlog.excerpt} />
        <meta name="keywords" content="STEMTRIX, robotics, STEM education, programming, student achievement, Kenya" />
        <meta property="og:title" content={currentBlog.title} />
        <meta property="og:description" content={currentBlog.excerpt} />
        <meta property="og:image" content={`https://stemtrix.co.ke${currentBlog.image}`} />
        <meta property="og:url" content={`https://stemtrix.co.ke/blog/${currentBlog.slug}`} />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href={`https://stemtrix.co.ke/blog/${currentBlog.slug}`} />
        */}
      </div>

      <div className="blog-container">
        {/* Navigation Header */}
        <nav className="blog-nav">
          <div className="nav-content">
            <button className="nav-back" onClick={() => window.history.back()}>
              <svg className="nav-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
              </svg>
              Back to Blog
            </button>
            <button className="nav-share" onClick={handleShare}>
              <svg className="nav-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"/>
              </svg>
              Share
            </button>
          </div>
        </nav>

        {/* Hero Section */}
        <header className="blog-hero">
          <div className="hero-content">
            <div className="category-badge">
              <svg className="badge-icon" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
              </svg>
              {currentBlog.category}
            </div>
            <h1 className="hero-title">
              Veer Gandhi Shines at International{" "}
              <span className="title-highlight">Robotics Competition</span>
            </h1>
            <p className="hero-subtitle">
              Representing Premier Academy and STEMTRIX at the prestigious International Student Robotics Challenge in Beijing, China
            </p>
          </div>
        </header>

        {/* Main Article */}
        <main className="blog-main">
          <article className="article-container">
            {/* Featured Image */}
            <div className="article-image-container">
              <img
                src={currentBlog.image}
                alt={`${currentBlog.title} - STEMTRIX Blog`}
                className="article-image"
                loading="eager"
              />
              <div className="image-overlay"></div>
              <div className="image-tags">
                {currentBlog.tags.map((tag, index) => (
                  <span key={index} className={`tag tag-${index % 4}`}>
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Article Content */}
            <div className="article-content">
              {/* Meta Information */}
              <div className="article-meta">
                <div className="meta-item">
                  <svg className="meta-icon" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd"/>
                  </svg>
                  {currentBlog.publishDate}
                </div>
                <div className="meta-item">
                  <svg className="meta-icon" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"/>
                  </svg>
                  {currentBlog.author}
                </div>
                <div className="meta-item">
                  <svg className="meta-icon" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd"/>
                  </svg>
                  {currentBlog.readTime}
                </div>
                <div className="meta-item">
                  <svg className="meta-icon" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd"/>
                  </svg>
                  {currentBlog.location}
                </div>
              </div>

              {/* Article Body */}
              <div className="article-body">
                <div className="highlight-quote">
                  This summer, <strong>Veer Gandhi</strong> proudly represented Premier Academy at the
                  prestigious <em>International Student Robotics Challenge</em> hosted by the University
                  of Science and Technology Beijing (USTB), competing against a global cohort of young innovators.
                </div>

                <div className="achievements-section">
                  <h3 className="section-title">
                    <svg className="section-icon" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                    </svg>
                    Competition Achievements
                  </h3>
                  <div className="achievements-grid">
                    <div className="achievement-card achievement-second">
                      <div className="achievement-rank">🥈</div>
                      <h4>RC.Code (Scratch Programming)</h4>
                      <p>Second Place - Elegant logic and intuitive interface design</p>
                    </div>
                    <div className="achievement-card achievement-third">
                      <div className="achievement-rank">🥉</div>
                      <h4>WeDo Construction</h4>
                      <p>Third Place - Mechanical design and creative storytelling</p>
                    </div>
                  </div>
                </div>

                <div 
                  className="content-text"
                  dangerouslySetInnerHTML={{ __html: formatContent(currentBlog.content) }}
                />

                <blockquote className="testimonial">
                  <p>
                    "Despite the language barrier and absence of his teammate, Veer adapted with remarkable 
                    courage and determination—managing both technical execution and presentation with exceptional poise."
                  </p>
                  <cite>— Competition Organizers</cite>
                </blockquote>

                {/* CTA Section */}
                <div className="cta-section">
                  <h3>Inspire Your Child's Future</h3>
                  <p>
                    Join STEMTRIX and give your child the opportunity to compete on the global stage, 
                    develop critical thinking skills, and prepare for tomorrow's technology careers.
                  </p>
                  <div className="cta-buttons">
                    <button className="btn btn-primary">Enroll Today</button>
                    <button className="btn btn-secondary">Learn More</button>
                  </div>
                </div>
              </div>
            </div>
          </article>

          {/* Related Articles */}
          <section className="related-articles">
            <h2 className="section-title-main">Related Articles</h2>
            <div className="related-grid">
              {relatedArticles.map((article) => (
                <article key={article.id} className="related-card">
                  <div className="related-image">
                    <div className="placeholder-image">
                      <svg fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd"/>
                      </svg>
                    </div>
                  </div>
                  <div className="related-content">
                    <h3>{article.title}</h3>
                    <p>{article.excerpt}</p>
                    <div className="related-meta">
                      <span>{article.date}</span>
                      <span>{article.readTime}</span>
                    </div>
                    <button className="read-more">Read More →</button>
                  </div>
                </article>
              ))}
            </div>
          </section>
        </main>
      </div>
    </>
  );
};

export default Blog;